import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  ListItem,
  TextField,
  Typography,
  Popover,
  popoverClasses,
} from "@analyticsMD/quil";
import { deepPurple, grey } from "@mui/material/colors";
import { Checkbox, IconButton } from "@mui/material";
import { Close, Search } from "@mui/icons-material";

const sxStyles = {
  button: {
    textTransform: "capitalize",
    fontWeight: "bold",
  },
  primaryButton: {
    backgroundColor: deepPurple[400],
  },
  secondaryButton: {
    color: deepPurple[400],
  },
  // hide the scrollbar until scroll
  scrollbar: {
    "&::-webkit-scrollbar": {
      width: "none",
      backgroundColor: "none",
    },
  },
  listItem: {
    paddingLeft: "0.5rem !important",
    paddingRight: "1rem !important",
    fontSize: "0.9rem",
    cursor: "pointer",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: "grey.50",
    },
  },
};

// Generic interface that any option type must implement
export interface SelectableOption {
  id: number | string;
  label: string;
}

interface SearchableSelectModalProps<T extends SelectableOption> {
  open: boolean;
  anchorElement: HTMLElement | null;
  options: T[];
  selectedValues?: T[];
  onCancel?: () => void;
  onSave?: (selectedValues: T[]) => void;
  onClose?: () => void;
  placeholder?: string;
  multiple?: boolean;
  width?: number | string;
  // For a popover opened from the keyboard: start in the search box and keep
  // focus inside until it closes. A click leaves focus where it was.
  focusOnOpen?: boolean;
}

function SearchableSelectModal<T extends SelectableOption>({
  open,
  anchorElement,
  options,
  selectedValues = [],
  onCancel,
  onSave,
  onClose,
  placeholder = "Search...",
  multiple = true,
  width,
  focusOnOpen = false,
}: SearchableSelectModalProps<T>) {
  const [newSelections, setNewSelections] = useState<T[]>(selectedValues);
  const [searchValue, setSearchValue] = useState<string>("");
  const newSelectionsRef = useRef<T[]>(selectedValues);

  // Update selections when selectedValues prop changes
  useEffect(() => {
    setNewSelections(selectedValues);
    newSelectionsRef.current = selectedValues;
  }, [selectedValues]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchValue("");
    }
  }, [open]);

  const handleClose = () => {
    setNewSelections(selectedValues);
    newSelectionsRef.current = selectedValues;
    setSearchValue("");
    onClose?.();
  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  if (!anchorElement) return null;

  return (
    <Popover
      elevation={0}
      open={open}
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      PaperProps={{
        variant: "outlined",
        role: "dialog",
        "aria-label": placeholder,
        style: {
          maxWidth: width || `${anchorElement.getBoundingClientRect().width}px`,
          maxHeight: "25rem",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          marginTop: "0.25rem",
        },
      }}
      sx={{
        [`.${popoverClasses.paper}`]: {
          borderRadius: "0.25rem",
        },
      }}
      onClose={handleClose}
      disableAutoFocus={!focusOnOpen}
      disableEnforceFocus={!focusOnOpen}
      disableRestoreFocus
    >
      {/* Search Input - Fixed at top */}
      <Box
        sx={{
          padding: "0.75rem",
          marginTop: ".25rem",
          backgroundColor: "white",
          [`& .MuiTextField-root`]: {
            padding: 0,
          },
        }}
      >
        <TextField
          fullWidth
          autoFocus={focusOnOpen}
          placeholder={placeholder}
          variant="filled"
          size="small"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            sx: { padding: "none" },
            endAdornment:
              searchValue.length > 0 ? (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              ) : (
                <Search
                  sx={{
                    color: grey[600],
                    pointerEvents: "none",
                  }}
                />
              ),
          }}
          sx={{
            [`& .MuiFilledInput-root`]: {
              backgroundColor: "white !important",
            },
          }}
        />
      </Box>

      {/* Selection Summary - if multiple selections */}
      {multiple && newSelections.length > 0 && (
        <Box
          sx={{
            padding: "0.5rem 1rem",
            backgroundColor: "grey.50",
          }}
        >
          <Typography variant="body2" color="primary">
            {newSelections.length} selected:{" "}
            {newSelections.map((s) => s.label).join(", ")}
          </Typography>
        </Box>
      )}

      {/* Options List - Scrollable */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          ...sxStyles.scrollbar,
        }}
      >
        {filteredOptions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No options found</Typography>
          </Box>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = newSelections.some((s) => s.id === option.id);
            return (
              <ListItem
                key={`option-${option.id}`}
                onClick={() => {
                  let newValue: T[];
                  if (multiple) {
                    if (isSelected) {
                      newValue = newSelections.filter(
                        (s) => s.id !== option.id
                      );
                    } else {
                      newValue = [...newSelections, option];
                    }
                  } else {
                    newValue = [option];
                  }
                  newSelectionsRef.current = newValue;
                  setNewSelections(newValue);
                }}
                sx={sxStyles.listItem}
              >
                {multiple && (
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      marginRight: "0.2rem",
                      color: grey[600],
                      "&.Mui-checked": {
                        color: deepPurple[600],
                      },
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    size="small"
                  />
                )}
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {option.label}
                </Typography>
              </ListItem>
            );
          })
        )}
      </Box>

      {/* Action Buttons - Fixed at bottom */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "1rem",
          gap: "0.5rem",
          fontWeight: "bold",
          backgroundColor: "white",
        }}
      >
        <Button
          size="small"
          sx={{ ...sxStyles.button, ...sxStyles.secondaryButton }}
          onClick={() => {
            setNewSelections(selectedValues);
            newSelectionsRef.current = selectedValues;
            setSearchValue("");
            onCancel?.();
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          sx={{
            ...sxStyles.button,
            ...sxStyles.primaryButton,
            textTransform: "none",
          }}
          onClick={() => {
            onSave?.(newSelectionsRef.current);
            setSearchValue("");
            onClose?.();
          }}
        >
          Apply filter
        </Button>
      </Box>
    </Popover>
  );
}

// Memoize the component to prevent unnecessary re-renders
const MemoizedSearchableSelectModal = React.memo(
  SearchableSelectModal,
  (prevProps, nextProps) => {
    const prevOptionsSignature = prevProps.options
      .map((option) => `${option.id}-${option.label}`)
      .join(",");
    const nextOptionsSignature = nextProps.options
      .map((option) => `${option.id}-${option.label}`)
      .join(",");

    return (
      prevProps.open === nextProps.open &&
      prevProps.focusOnOpen === nextProps.focusOnOpen &&
      prevOptionsSignature === nextOptionsSignature &&
      JSON.stringify(prevProps.selectedValues) ===
        JSON.stringify(nextProps.selectedValues)
    );
  }
) as typeof SearchableSelectModal;

export default MemoizedSearchableSelectModal;
